import { gql, NetworkStatus, useLazyQuery } from '@apollo/client';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import {
  DashboardViewFragment,
  DashboardViewFragmentDoc,
  GetDashboardByIdInput,
  Query,
} from 'generated/graphql';
import { useCallback, useRef } from 'react';
import ErrorCode from 'v2/core/errors/codes';
import { isErrorCode } from 'v2/core/errors/errors';
import { useGetUser } from 'v2/core/services/user';

interface useFetchDashboardParams {
  onCompleted?: (dashboard: DashboardViewFragment | undefined) => void;
}

const GET_DASHBOARD_BY_ID = gql`
  query getDashboardByID($id: ID!, $tenantID: ID!, $userID: ID!) {
    getDashboardByID(
      request: { id: $id, tenantID: $tenantID, userID: $userID }
    ) {
      dashboard {
        ...DashboardView
      }
    }
  }
  ${DashboardViewFragmentDoc}
`;

export const useFetchDashboardById = ({
  onCompleted,
}: useFetchDashboardParams) => {
  const {
    id: userID,
    tenant: { id: tenantID },
  } = useGetUser();
  const { showErrorNotification } = useNotificationContext();

  const dependencies = { userID, tenantID };
  const dependenciesRef = useRef(dependencies);
  dependenciesRef.current = dependencies;

  const [
    getDashboardByIDQuery,
    { data, networkStatus, loading, startPolling, stopPolling },
  ] = useLazyQuery<Query, GetDashboardByIdInput>(GET_DASHBOARD_BY_ID, {
    onError: (error) => {
      if (!error) {
        return;
      }

      const isRecordNotFoundError = isErrorCode(
        error,
        ErrorCode.RecordNotFound,
      );
      if (isRecordNotFoundError) {
        showErrorNotification({
          contentComponent: 'Dashboard not found',
          props: {},
        });
        return;
      }

      showErrorNotification({
        contentComponent: 'Failed to fetch the dashboard',
        props: {},
      });
    },
    onCompleted: (data) => {
      // ##TODO, remove the type casting
      onCompleted?.(
        data?.getDashboardByID?.dashboard as DashboardViewFragment | undefined,
      );
    },
  });

  const getDashboardByID = useCallback(
    (id: string | undefined) => {
      if (!id) {
        return;
      }

      getDashboardByIDQuery({
        variables: {
          id,
          tenantID: dependenciesRef.current.tenantID,
          userID: dependenciesRef.current.userID,
        },
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
        nextFetchPolicy: 'cache-first',
      });
    },
    [getDashboardByIDQuery],
  );

  // ##TODO, remove the type casting
  const dashboard = data?.getDashboardByID?.dashboard as
    | DashboardViewFragment
    | undefined;

  const polling = networkStatus === NetworkStatus.poll;

  return [
    getDashboardByID,
    {
      dashboard,
      loading,
      polling,
      startPolling,
      stopPolling,
    },
  ] as const;
};
